import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { fromCents, toPositive } from '@ankor-io/common/domain/pay/currency'
import type { CardSpendingStatsPayload } from '@ankor-io/common/types/cards'
import { formatCurrency } from '@ankor-io/common/util/currencies'

type CardSpendChartProps = {
  stats: CardSpendingStatsPayload
  selectedCurrency: string
}

export const CardSpendChart = ({ stats, selectedCurrency }: CardSpendChartProps) => {
  // Get the current currency to use for chart (selected or default)
  const getChartCurrency = () => {
    const availableCurrencies = stats?.last7Days?.currencies || ['USD']
    return selectedCurrency || availableCurrencies[0] || 'USD'
  }

  // Custom tooltip for the chart
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean
    payload?: Array<{ value: number; dataKey: string }>
    label?: string
  }) => {
    if (active && payload && payload.length) {
      const chartCurrency = getChartCurrency()
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatCurrency(payload[0].value, chartCurrency)}
          </p>
        </div>
      )
    }
    return null
  }

  // Transform stats data for the chart
  const transformStatsForChart = () => {
    if (!stats?.last7Days?.days) return []

    const chartData = []
    const today = new Date()
    const chartCurrency = getChartCurrency()

    // Create data for the last 7 days
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today)
      date.setDate(date.getDate() - i)

      const dayKey = i.toString()
      const dayStats = stats.last7Days.days[dayKey]

      const dayAmount = dayStats?.[chartCurrency]?.totalSpentCents || 0

      chartData.push({
        date: date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
        }),
        amount: toPositive(fromCents(dayAmount)), // Convert cents to dollars
      })
    }

    return chartData
  }

  const chartData = transformStatsForChart()

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer
        width="100%"
        height="100%"
      >
        <BarChart data={chartData}>
          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
            tickFormatter={(value) => `${value}`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="amount"
            fill="var(--color-primary-500)"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default CardSpendChart
